import { useMemo, useState } from 'react';

import { chartColor, isDarkMode } from './palette';

export interface BarChartDatum {
    label: string;
    value: number;
    /** Optional fixed series slot (color). Defaults to slot 0 for single-hue charts. */
    colorIndex?: number;
}

interface BarChartProps {
    data: BarChartDatum[];
    /** Bar height in px */
    barSize?: number;
    /** Format the value shown in labels/tooltips */
    formatValue?: (value: number) => string;
    /** Max bars before folding the tail into "Other" */
    maxBars?: number;
    className?: string;
}

/**
 * Horizontal bar chart: thin rounded marks anchored to the baseline, value
 * labels in text ink, per-bar hover highlight. Pure SVG/HTML — no deps.
 */
const BarChart: React.FC<BarChartProps> = ({ data, barSize = 22, formatValue = (v) => String(v), maxBars = 8, className = '' }) => {
    const dark = isDarkMode();
    const [hovered, setHovered] = useState<number | null>(null);

    const rows = useMemo(() => {
        if (data.length <= maxBars) return data;
        const head = data.slice(0, maxBars - 1);
        const tail = data.slice(maxBars - 1);
        return [...head, { label: `Other (${tail.length})`, value: tail.reduce((sum, d) => sum + d.value, 0) }];
    }, [data, maxBars]);

    const max = Math.max(1, ...rows.map((d) => d.value));

    if (rows.length === 0) {
        return <div className={`text-sm text-slate-400 dark:text-slate-500 text-center py-6 ${className}`}>No data yet</div>;
    }

    return (
        <div className={`space-y-2 ${className}`} role="img" aria-label="Bar chart">
            {rows.map((d, i) => {
                const widthPct = Math.max(1, (d.value / max) * 100);
                const color = chartColor(d.colorIndex ?? 0, dark);
                return (
                    <div
                        key={`${d.label}-${i}`}
                        className="group flex items-center gap-3 cursor-default"
                        onMouseEnter={() => setHovered(i)}
                        onMouseLeave={() => setHovered(null)}
                    >
                        <div className="w-32 shrink-0 text-xs text-slate-600 dark:text-slate-400 truncate text-right" title={d.label}>
                            {d.label}
                        </div>
                        <div className="flex-1 h-full relative" style={{ height: barSize }}>
                            <div className="absolute inset-0 rounded bg-slate-100 dark:bg-slate-700/50" />
                            <div
                                className="absolute inset-y-0 left-0 rounded transition-all duration-300"
                                style={{
                                    width: `${widthPct}%`,
                                    backgroundColor: color,
                                    opacity: hovered === null || hovered === i ? 1 : 0.45,
                                }}
                            />
                        </div>
                        <div className="w-14 shrink-0 text-xs font-medium text-slate-900 dark:text-white tabular-nums">
                            {formatValue(d.value)}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default BarChart;
